/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/**
 * Content model for the PMF feedback prompt: which feature radios the GENERAL
 * prompt shows, which option checkboxes the form shows per feature event and
 * rating, plus the PhraseApp key and English fallback behind each option.
 *
 * This module is intentionally dependency-free (it imports nothing from the
 * PmfFeedback folder) so both `PmfFeedbackContent` and `PmfFeedbackProvider`
 * can read it without creating an import cycle.
 */

/**
 * Events that can trigger the PMF feedback prompt.
 *
 * `GENERAL` is special: it always opens the feedback immediately and never
 * touches localStorage (the "user tapped Give Feedback" path). Every other
 * event shares one per-user counter in localStorage — see `pmfFeedbackStorage.ts`.
 */
export const PMF_FEEDBACK_EVENT = {
  GENERAL: "GENERAL",
  ADD_PRODUCT: "ADD_PRODUCT",
  UPDATE_PRODUCT: "UPDATE_PRODUCT",
  UPDATE_STOCK: "UPDATE_STOCK",
  BULK_ADD_PRODUCT: "BULK_ADD_PRODUCT",
  BULK_UPDATE_STOCK: "BULK_UPDATE_STOCK",
} as const

export type PmfFeedbackEvent = typeof PMF_FEEDBACK_EVENT[keyof typeof PMF_FEEDBACK_EVENT]

/**
 * A feature a merchant can give GENERAL feedback about. Each feature is a
 * feature event, so the feature's topic options are simply that event's
 * option list — no separate mapping table to keep in sync.
 */
export type PmfFeedbackFeature = Exclude<
  PmfFeedbackEvent,
  typeof PMF_FEEDBACK_EVENT.GENERAL
>

/** Ordered feature radios for the GENERAL prompt (design order). */
export const PMF_FEEDBACK_FEATURES: PmfFeedbackFeature[] = [
  PMF_FEEDBACK_EVENT.ADD_PRODUCT,
  PMF_FEEDBACK_EVENT.UPDATE_PRODUCT,
  PMF_FEEDBACK_EVENT.UPDATE_STOCK,
  PMF_FEEDBACK_EVENT.BULK_ADD_PRODUCT,
  PMF_FEEDBACK_EVENT.BULK_UPDATE_STOCK,
]

/**
 * The "Others" radio — not a feature event, so it has no topic options. Picking
 * it hides the topic options and leaves only the free-text input.
 */
export const PMF_FEEDBACK_FEATURE_OTHERS = "OTHERS" as const

/** A value selectable in the GENERAL prompt's feature radio group. */
export type PmfFeedbackFeatureChoice =
  | PmfFeedbackFeature
  | typeof PMF_FEEDBACK_FEATURE_OTHERS

/** Ordered radio values for the GENERAL prompt — the features, then "Others". */
export const PMF_FEEDBACK_FEATURE_CHOICES: PmfFeedbackFeatureChoice[] = [
  ...PMF_FEEDBACK_FEATURES,
  PMF_FEEDBACK_FEATURE_OTHERS,
]

/**
 * Narrows a triggering event or a chosen feature to a feature event — false for
 * GENERAL (no feature picked) and for "Others" (no topic options).
 */
export const isPmfFeedbackFeatureEvent = (
  value: PmfFeedbackEvent | PmfFeedbackFeatureChoice,
): value is PmfFeedbackFeature =>
  value !== PMF_FEEDBACK_EVENT.GENERAL && value !== PMF_FEEDBACK_FEATURE_OTHERS

export const PMF_FEEDBACK_RATING = {
  GOOD: "GOOD",
  BAD: "BAD",
} as const

export type PmfFeedbackRating = typeof PMF_FEEDBACK_RATING[keyof typeof PMF_FEEDBACK_RATING]

/**
 * Stable tracking IDs for the feature events' options, sent to analytics in
 * the `REASONS` label.
 *
 * One ID per distinct option wording — the event and rating are already carried
 * by the `TRIGGERING_EVENT` and `SENTIMENT` labels, so they are not encoded
 * here, and events that offer the same wording share the ID. Treat these as
 * opaque keys: copy edits change the PhraseApp string, never the ID.
 */
export const PMF_FEEDBACK_OPTION = {
  ADDING_MANY_PRODUCTS_AT_A_TIME_IS_STRAIGHTFORWARD:
    "ADDING_MANY_PRODUCTS_AT_A_TIME_IS_STRAIGHTFORWARD",
  ADDING_PHOTOS_HAS_LIMITATIONS: "ADDING_PHOTOS_HAS_LIMITATIONS",
  ADDING_VIDEO_HAS_LIMITATIONS: "ADDING_VIDEO_HAS_LIMITATIONS",
  CANNOT_HIDE_THE_PRODUCT_LISTING_FROM_MY_STORE:
    "CANNOT_HIDE_THE_PRODUCT_LISTING_FROM_MY_STORE",
  CANNOT_UPDATE_STOCK_FROM_THE_PRODUCTS_PAGE:
    "CANNOT_UPDATE_STOCK_FROM_THE_PRODUCTS_PAGE",
  CATEGORY_AND_VARIATION_SETUP_IS_TOO_PAINFUL:
    "CATEGORY_AND_VARIATION_SETUP_IS_TOO_PAINFUL",
  EASY_TO_ADD_MORE_PHOTOS: "EASY_TO_ADD_MORE_PHOTOS",
  EASY_TO_CHANGE_STOCK_VALUES: "EASY_TO_CHANGE_STOCK_VALUES",
  EASY_TO_CREATE: "EASY_TO_CREATE",
  EASY_TO_EDIT_DESCRIPTION: "EASY_TO_EDIT_DESCRIPTION",
  EASY_TO_MANAGE_STOCK_ACROSS_ALL_STORES:
    "EASY_TO_MANAGE_STOCK_ACROSS_ALL_STORES",
  EASY_TO_NAVIGATE: "EASY_TO_NAVIGATE",
  EASY_TO_UPLOAD_VIDEO: "EASY_TO_UPLOAD_VIDEO",
  ERRORS_WHEN_CREATING_PRODUCT: "ERRORS_WHEN_CREATING_PRODUCT",
  ERRORS_WHEN_EDITING_PRODUCTS: "ERRORS_WHEN_EDITING_PRODUCTS",
  ERRORS_WHEN_ADDING_PRODUCTS: "ERRORS_WHEN_ADDING_PRODUCTS",
  ERRORS_WHEN_UPDATING_STOCK: "ERRORS_WHEN_UPDATING_STOCK",
  ERRORS_WHEN_UPLOADING_PHOTOS_VIDEOS: "ERRORS_WHEN_UPLOADING_PHOTOS_VIDEOS",
  HAS_PRODUCT_VARIATIONS: "HAS_PRODUCT_VARIATIONS",
  INTEGRATION_WITH_MY_CATALOG_MANAGEMENT_SYSTEM_IS_NOT_AVAILABLE:
    "INTEGRATION_WITH_MY_CATALOG_MANAGEMENT_SYSTEM_IS_NOT_AVAILABLE",
  INTEGRATION_WITH_MY_INVENTORY_MANAGEMENT_SYSTEM_IS_NOT_AVAILABLE:
    "INTEGRATION_WITH_MY_INVENTORY_MANAGEMENT_SYSTEM_IS_NOT_AVAILABLE",
  MARKING_OUT_OF_STOCK_IS_NOT_EASY: "MARKING_OUT_OF_STOCK_IS_NOT_EASY",
  MISSING_PRODUCT_CATEGORIES: "MISSING_PRODUCT_CATEGORIES",
  NOT_SURE_WHY_IT_SAYS_REVISION_REQUIRED:
    "NOT_SURE_WHY_IT_SAYS_REVISION_REQUIRED",
  PRODUCT_VARIATION_TYPES_OPTIONS_NOT_AVAILABLE:
    "PRODUCT_VARIATION_TYPES_OPTIONS_NOT_AVAILABLE",
  SETTING_UNLIMITED_STOCK_IS_NOT_POSSIBLE:
    "SETTING_UNLIMITED_STOCK_IS_NOT_POSSIBLE",
  SUPPORTS_MULTIPLE_PHOTOS: "SUPPORTS_MULTIPLE_PHOTOS",
  SUPPORTS_VIDEO: "SUPPORTS_VIDEO",
  TEMPLATE_IS_EASY_TO_USE: "TEMPLATE_IS_EASY_TO_USE",
  TOO_COMPLEX: "TOO_COMPLEX",
  TOO_MANY_INPUTS_REQUIRED: "TOO_MANY_INPUTS_REQUIRED",
  TOO_MANY_STEPS: "TOO_MANY_STEPS",
  UPDATING_PRICE_AND_STOCK_IS_EASY: "UPDATING_PRICE_AND_STOCK_IS_EASY",
  UPDATING_STOCK_ACROSS_STORES_IS_STRAIGHTFORWARD:
    "UPDATING_STOCK_ACROSS_STORES_IS_STRAIGHTFORWARD",
  UPDATING_STOCK_FOR_ONE_ITEM_AT_A_TIME_IS_PAINFUL:
    "UPDATING_STOCK_FOR_ONE_ITEM_AT_A_TIME_IS_PAINFUL",
} as const

export type PmfFeedbackOption = typeof PMF_FEEDBACK_OPTION[keyof typeof PMF_FEEDBACK_OPTION]

/** One selectable topic option — a feature option. (GENERAL's feature radios are separate; see `PmfFeedbackFeatureChoice`.) */
export type PmfFeedbackSelection = PmfFeedbackOption

/**
 * Which checkboxes the form shows, per feature event and rating.
 *
 * There is no GENERAL entry: the GENERAL prompt resolves the list from the
 * feature the merchant picked (see `PMF_FEEDBACK_FEATURE_CHOICES`), so the type
 * itself makes a static GENERAL list impossible.
 */
export const PMF_FEEDBACK_OPTIONS: Record<
  PmfFeedbackFeature,
  Record<PmfFeedbackRating, PmfFeedbackSelection[]>
> = {
  [PMF_FEEDBACK_EVENT.ADD_PRODUCT]: {
    [PMF_FEEDBACK_RATING.GOOD]: [
      PMF_FEEDBACK_OPTION.SUPPORTS_MULTIPLE_PHOTOS,
      PMF_FEEDBACK_OPTION.SUPPORTS_VIDEO,
      PMF_FEEDBACK_OPTION.HAS_PRODUCT_VARIATIONS,
      PMF_FEEDBACK_OPTION.EASY_TO_CREATE,
    ],
    [PMF_FEEDBACK_RATING.BAD]: [
      PMF_FEEDBACK_OPTION.ADDING_PHOTOS_HAS_LIMITATIONS,
      PMF_FEEDBACK_OPTION.ADDING_VIDEO_HAS_LIMITATIONS,
      PMF_FEEDBACK_OPTION.ERRORS_WHEN_CREATING_PRODUCT,
      PMF_FEEDBACK_OPTION.PRODUCT_VARIATION_TYPES_OPTIONS_NOT_AVAILABLE,
      PMF_FEEDBACK_OPTION.MISSING_PRODUCT_CATEGORIES,
      PMF_FEEDBACK_OPTION.TOO_MANY_STEPS,
    ],
  },
  [PMF_FEEDBACK_EVENT.UPDATE_PRODUCT]: {
    [PMF_FEEDBACK_RATING.GOOD]: [
      PMF_FEEDBACK_OPTION.EASY_TO_ADD_MORE_PHOTOS,
      PMF_FEEDBACK_OPTION.EASY_TO_UPLOAD_VIDEO,
      PMF_FEEDBACK_OPTION.EASY_TO_EDIT_DESCRIPTION,
      PMF_FEEDBACK_OPTION.UPDATING_PRICE_AND_STOCK_IS_EASY,
    ],
    [PMF_FEEDBACK_RATING.BAD]: [
      PMF_FEEDBACK_OPTION.ADDING_PHOTOS_HAS_LIMITATIONS,
      PMF_FEEDBACK_OPTION.ADDING_VIDEO_HAS_LIMITATIONS,
      PMF_FEEDBACK_OPTION.ERRORS_WHEN_EDITING_PRODUCTS,
      PMF_FEEDBACK_OPTION.PRODUCT_VARIATION_TYPES_OPTIONS_NOT_AVAILABLE,
      PMF_FEEDBACK_OPTION.MISSING_PRODUCT_CATEGORIES,
      PMF_FEEDBACK_OPTION.NOT_SURE_WHY_IT_SAYS_REVISION_REQUIRED,
    ],
  },
  [PMF_FEEDBACK_EVENT.UPDATE_STOCK]: {
    [PMF_FEEDBACK_RATING.GOOD]: [
      PMF_FEEDBACK_OPTION.EASY_TO_NAVIGATE,
      PMF_FEEDBACK_OPTION.EASY_TO_MANAGE_STOCK_ACROSS_ALL_STORES,
      PMF_FEEDBACK_OPTION.EASY_TO_CHANGE_STOCK_VALUES,
    ],
    [PMF_FEEDBACK_RATING.BAD]: [
      PMF_FEEDBACK_OPTION.CANNOT_UPDATE_STOCK_FROM_THE_PRODUCTS_PAGE,
      PMF_FEEDBACK_OPTION.MARKING_OUT_OF_STOCK_IS_NOT_EASY,
      PMF_FEEDBACK_OPTION.SETTING_UNLIMITED_STOCK_IS_NOT_POSSIBLE,
      PMF_FEEDBACK_OPTION.CANNOT_HIDE_THE_PRODUCT_LISTING_FROM_MY_STORE,
      PMF_FEEDBACK_OPTION.UPDATING_STOCK_FOR_ONE_ITEM_AT_A_TIME_IS_PAINFUL,
    ],
  },
  [PMF_FEEDBACK_EVENT.BULK_ADD_PRODUCT]: {
    [PMF_FEEDBACK_RATING.GOOD]: [
      PMF_FEEDBACK_OPTION.TEMPLATE_IS_EASY_TO_USE,
      PMF_FEEDBACK_OPTION.ADDING_MANY_PRODUCTS_AT_A_TIME_IS_STRAIGHTFORWARD,
    ],
    [PMF_FEEDBACK_RATING.BAD]: [
      PMF_FEEDBACK_OPTION.TOO_COMPLEX,
      PMF_FEEDBACK_OPTION.TOO_MANY_INPUTS_REQUIRED,
      PMF_FEEDBACK_OPTION.ERRORS_WHEN_ADDING_PRODUCTS,
      PMF_FEEDBACK_OPTION.ERRORS_WHEN_UPLOADING_PHOTOS_VIDEOS,
      PMF_FEEDBACK_OPTION.CATEGORY_AND_VARIATION_SETUP_IS_TOO_PAINFUL,
      PMF_FEEDBACK_OPTION.INTEGRATION_WITH_MY_CATALOG_MANAGEMENT_SYSTEM_IS_NOT_AVAILABLE,
    ],
  },
  [PMF_FEEDBACK_EVENT.BULK_UPDATE_STOCK]: {
    [PMF_FEEDBACK_RATING.GOOD]: [
      PMF_FEEDBACK_OPTION.TEMPLATE_IS_EASY_TO_USE,
      PMF_FEEDBACK_OPTION.UPDATING_STOCK_ACROSS_STORES_IS_STRAIGHTFORWARD,
    ],
    [PMF_FEEDBACK_RATING.BAD]: [
      PMF_FEEDBACK_OPTION.TOO_COMPLEX,
      PMF_FEEDBACK_OPTION.ERRORS_WHEN_UPDATING_STOCK,
      PMF_FEEDBACK_OPTION.INTEGRATION_WITH_MY_INVENTORY_MANAGEMENT_SYSTEM_IS_NOT_AVAILABLE,
    ],
  },
}

/**
 * Selection → PhraseApp locale path. The `Record` annotation keeps this map
 * exhaustive: adding an option without a key fails type-check.
 */
export const PMF_FEEDBACK_SELECTION_I18N_PATHS: Record<
  PmfFeedbackSelection,
  string
> = {
  [PMF_FEEDBACK_OPTION.ADDING_MANY_PRODUCTS_AT_A_TIME_IS_STRAIGHTFORWARD]:
    "ecommerce.sellerCentre.pmfFeedback.options.addingManyProductsAtATimeIsStraightforward",
  [PMF_FEEDBACK_OPTION.ADDING_PHOTOS_HAS_LIMITATIONS]:
    "ecommerce.sellerCentre.pmfFeedback.options.addingPhotosHasLimitations",
  [PMF_FEEDBACK_OPTION.ADDING_VIDEO_HAS_LIMITATIONS]:
    "ecommerce.sellerCentre.pmfFeedback.options.addingVideoHasLimitations",
  [PMF_FEEDBACK_OPTION.CANNOT_HIDE_THE_PRODUCT_LISTING_FROM_MY_STORE]:
    "ecommerce.sellerCentre.pmfFeedback.options.cannotHideTheProductListingFromMyStore",
  [PMF_FEEDBACK_OPTION.CANNOT_UPDATE_STOCK_FROM_THE_PRODUCTS_PAGE]:
    "ecommerce.sellerCentre.pmfFeedback.options.cannotUpdateStockFromTheProductsPage",
  [PMF_FEEDBACK_OPTION.CATEGORY_AND_VARIATION_SETUP_IS_TOO_PAINFUL]:
    "ecommerce.sellerCentre.pmfFeedback.options.categoryAndVariationSetupIsTooPainful",
  [PMF_FEEDBACK_OPTION.EASY_TO_ADD_MORE_PHOTOS]:
    "ecommerce.sellerCentre.pmfFeedback.options.easyToAddMorePhotos",
  [PMF_FEEDBACK_OPTION.EASY_TO_CHANGE_STOCK_VALUES]:
    "ecommerce.sellerCentre.pmfFeedback.options.easyToChangeStockValues",
  [PMF_FEEDBACK_OPTION.EASY_TO_CREATE]:
    "ecommerce.sellerCentre.pmfFeedback.options.easyToCreate",
  [PMF_FEEDBACK_OPTION.EASY_TO_EDIT_DESCRIPTION]:
    "ecommerce.sellerCentre.pmfFeedback.options.easyToEditDescription",
  [PMF_FEEDBACK_OPTION.EASY_TO_MANAGE_STOCK_ACROSS_ALL_STORES]:
    "ecommerce.sellerCentre.pmfFeedback.options.easyToManageStockAcrossAllStores",
  [PMF_FEEDBACK_OPTION.EASY_TO_NAVIGATE]:
    "ecommerce.sellerCentre.pmfFeedback.options.easyToNavigate",
  [PMF_FEEDBACK_OPTION.EASY_TO_UPLOAD_VIDEO]:
    "ecommerce.sellerCentre.pmfFeedback.options.easyToUploadVideo",
  [PMF_FEEDBACK_OPTION.ERRORS_WHEN_CREATING_PRODUCT]:
    "ecommerce.sellerCentre.pmfFeedback.options.errorsWhenCreatingProduct",
  [PMF_FEEDBACK_OPTION.ERRORS_WHEN_EDITING_PRODUCTS]:
    "ecommerce.sellerCentre.pmfFeedback.options.errorsWhenEditingProducts",
  [PMF_FEEDBACK_OPTION.ERRORS_WHEN_ADDING_PRODUCTS]:
    "ecommerce.sellerCentre.pmfFeedback.options.facedErrorsWhenAddingProducts",
  [PMF_FEEDBACK_OPTION.ERRORS_WHEN_UPDATING_STOCK]:
    "ecommerce.sellerCentre.pmfFeedback.options.facedErrorsWhenUpdatingStock",
  [PMF_FEEDBACK_OPTION.ERRORS_WHEN_UPLOADING_PHOTOS_VIDEOS]:
    "ecommerce.sellerCentre.pmfFeedback.options.facedErrorsWhenUploadingPhotosVideos",
  [PMF_FEEDBACK_OPTION.HAS_PRODUCT_VARIATIONS]:
    "ecommerce.sellerCentre.pmfFeedback.options.hasProductVariations",
  [PMF_FEEDBACK_OPTION.INTEGRATION_WITH_MY_CATALOG_MANAGEMENT_SYSTEM_IS_NOT_AVAILABLE]:
    "ecommerce.sellerCentre.pmfFeedback.options.integrationWithMyCatalogManagementSystemIsNotAvailable",
  [PMF_FEEDBACK_OPTION.INTEGRATION_WITH_MY_INVENTORY_MANAGEMENT_SYSTEM_IS_NOT_AVAILABLE]:
    "ecommerce.sellerCentre.pmfFeedback.options.integrationWithMyInventoryManagementSystemIsNotAvailable",
  [PMF_FEEDBACK_OPTION.MARKING_OUT_OF_STOCK_IS_NOT_EASY]:
    "ecommerce.sellerCentre.pmfFeedback.options.markingOutOfStockIsNotEasy",
  [PMF_FEEDBACK_OPTION.MISSING_PRODUCT_CATEGORIES]:
    "ecommerce.sellerCentre.pmfFeedback.options.missingProductCategories",
  [PMF_FEEDBACK_OPTION.NOT_SURE_WHY_IT_SAYS_REVISION_REQUIRED]:
    "ecommerce.sellerCentre.pmfFeedback.options.notSureWhyItSaysRevisionRequired",
  [PMF_FEEDBACK_OPTION.PRODUCT_VARIATION_TYPES_OPTIONS_NOT_AVAILABLE]:
    "ecommerce.sellerCentre.pmfFeedback.options.productVariationTypesOptionsNotAvailable",
  [PMF_FEEDBACK_OPTION.SETTING_UNLIMITED_STOCK_IS_NOT_POSSIBLE]:
    "ecommerce.sellerCentre.pmfFeedback.options.settingUnlimitedStockIsNotPossible",
  [PMF_FEEDBACK_OPTION.SUPPORTS_MULTIPLE_PHOTOS]:
    "ecommerce.sellerCentre.pmfFeedback.options.supportsMultiplePhotos",
  [PMF_FEEDBACK_OPTION.SUPPORTS_VIDEO]:
    "ecommerce.sellerCentre.pmfFeedback.options.supportsVideo",
  [PMF_FEEDBACK_OPTION.TEMPLATE_IS_EASY_TO_USE]:
    "ecommerce.sellerCentre.pmfFeedback.options.templateIsEasyToUse",
  [PMF_FEEDBACK_OPTION.TOO_COMPLEX]:
    "ecommerce.sellerCentre.pmfFeedback.options.tooComplex",
  [PMF_FEEDBACK_OPTION.TOO_MANY_INPUTS_REQUIRED]:
    "ecommerce.sellerCentre.pmfFeedback.options.tooManyInputsRequired",
  [PMF_FEEDBACK_OPTION.TOO_MANY_STEPS]:
    "ecommerce.sellerCentre.pmfFeedback.options.tooManySteps",
  [PMF_FEEDBACK_OPTION.UPDATING_PRICE_AND_STOCK_IS_EASY]:
    "ecommerce.sellerCentre.pmfFeedback.options.updatingPriceAndStockIsEasy",
  [PMF_FEEDBACK_OPTION.UPDATING_STOCK_ACROSS_STORES_IS_STRAIGHTFORWARD]:
    "ecommerce.sellerCentre.pmfFeedback.options.updatingStockAcrossStoresIsStraightforward",
  [PMF_FEEDBACK_OPTION.UPDATING_STOCK_FOR_ONE_ITEM_AT_A_TIME_IS_PAINFUL]:
    "ecommerce.sellerCentre.pmfFeedback.options.updatingStockForOneItemAtATimeIsPainful",
}

/**
 * Feature radio value → PhraseApp locale path. The `Record` annotation keeps
 * this map exhaustive over the GENERAL prompt's radios.
 */
export const PMF_FEEDBACK_FEATURE_I18N_PATHS: Record<
  PmfFeedbackFeatureChoice,
  string
> = {
  [PMF_FEEDBACK_EVENT.ADD_PRODUCT]:
    "ecommerce.sellerCentre.pmfFeedback.features.creatingAProduct",
  [PMF_FEEDBACK_EVENT.UPDATE_PRODUCT]:
    "ecommerce.sellerCentre.pmfFeedback.features.editingAProduct",
  [PMF_FEEDBACK_EVENT.UPDATE_STOCK]:
    "ecommerce.sellerCentre.pmfFeedback.features.updatingStocks",
  [PMF_FEEDBACK_EVENT.BULK_ADD_PRODUCT]:
    "ecommerce.sellerCentre.pmfFeedback.features.addingProductsInBulk",
  [PMF_FEEDBACK_EVENT.BULK_UPDATE_STOCK]:
    "ecommerce.sellerCentre.pmfFeedback.features.updatingStocksInBulk",
  [PMF_FEEDBACK_FEATURE_OTHERS]:
    "ecommerce.sellerCentre.pmfFeedback.features.others",
}

/**
 * TEMPORARY (BP-63014) — English fallbacks for the option labels.
 *
 * The PhraseApp keys behind these options exist and are mapped in
 * `scripts/phraseapp/phraseapp_config.json`, but until the copy is published
 * and `phrase pull` regenerates `public/locales/*`, `translate` resolves them
 * to "" and every checkbox would render with a blank label. Wrapping each
 * lookup in `translationOrFallback` keeps the English readable until then,
 * after which the translation takes over automatically.
 *
 * TODO(BP-63014): delete this object and read `selectionLabels` directly once
 * the copy is published and pulled. Confirm with
 * `yarn check-missing-locales:fresh --strict`.
 */
export const PMF_FEEDBACK_SELECTION_FALLBACKS: Record<
  PmfFeedbackSelection,
  string
> = {
  [PMF_FEEDBACK_OPTION.ADDING_MANY_PRODUCTS_AT_A_TIME_IS_STRAIGHTFORWARD]:
    "Adding many products at a time is straightforward",
  [PMF_FEEDBACK_OPTION.ADDING_PHOTOS_HAS_LIMITATIONS]:
    "Adding photos has limitations",
  [PMF_FEEDBACK_OPTION.ADDING_VIDEO_HAS_LIMITATIONS]:
    "Adding video has limitations",
  [PMF_FEEDBACK_OPTION.CANNOT_HIDE_THE_PRODUCT_LISTING_FROM_MY_STORE]:
    "Cannot hide the product listing from my store",
  [PMF_FEEDBACK_OPTION.CANNOT_UPDATE_STOCK_FROM_THE_PRODUCTS_PAGE]:
    "Cannot update stock from the Products page",
  [PMF_FEEDBACK_OPTION.CATEGORY_AND_VARIATION_SETUP_IS_TOO_PAINFUL]:
    "Category and variation setup is too painful",
  [PMF_FEEDBACK_OPTION.EASY_TO_ADD_MORE_PHOTOS]: "Easy to add more photos",
  [PMF_FEEDBACK_OPTION.EASY_TO_CHANGE_STOCK_VALUES]:
    "Easy to change stock values",
  [PMF_FEEDBACK_OPTION.EASY_TO_CREATE]: "Easy to create",
  [PMF_FEEDBACK_OPTION.EASY_TO_EDIT_DESCRIPTION]: "Easy to edit description",
  [PMF_FEEDBACK_OPTION.EASY_TO_MANAGE_STOCK_ACROSS_ALL_STORES]:
    "Easy to manage stock across all stores",
  [PMF_FEEDBACK_OPTION.EASY_TO_NAVIGATE]: "Easy to navigate",
  [PMF_FEEDBACK_OPTION.EASY_TO_UPLOAD_VIDEO]: "Easy to upload video",
  [PMF_FEEDBACK_OPTION.ERRORS_WHEN_CREATING_PRODUCT]:
    "Errors when creating product",
  [PMF_FEEDBACK_OPTION.ERRORS_WHEN_EDITING_PRODUCTS]:
    "Errors when editing products",
  [PMF_FEEDBACK_OPTION.ERRORS_WHEN_ADDING_PRODUCTS]:
    "Faced errors when adding products",
  [PMF_FEEDBACK_OPTION.ERRORS_WHEN_UPDATING_STOCK]:
    "Faced errors when updating stock",
  [PMF_FEEDBACK_OPTION.ERRORS_WHEN_UPLOADING_PHOTOS_VIDEOS]:
    "Faced errors when uploading photos/videos",
  [PMF_FEEDBACK_OPTION.HAS_PRODUCT_VARIATIONS]: "Has product variations",
  [PMF_FEEDBACK_OPTION.INTEGRATION_WITH_MY_CATALOG_MANAGEMENT_SYSTEM_IS_NOT_AVAILABLE]:
    "Integration with my catalog management system is not available",
  [PMF_FEEDBACK_OPTION.INTEGRATION_WITH_MY_INVENTORY_MANAGEMENT_SYSTEM_IS_NOT_AVAILABLE]:
    "Integration with my inventory management system is not available",
  [PMF_FEEDBACK_OPTION.MARKING_OUT_OF_STOCK_IS_NOT_EASY]:
    "Marking out of stock is not easy",
  [PMF_FEEDBACK_OPTION.MISSING_PRODUCT_CATEGORIES]:
    "Missing product categories",
  [PMF_FEEDBACK_OPTION.NOT_SURE_WHY_IT_SAYS_REVISION_REQUIRED]:
    "Not sure why it says 'Revision Required'",
  [PMF_FEEDBACK_OPTION.PRODUCT_VARIATION_TYPES_OPTIONS_NOT_AVAILABLE]:
    "Product variation types/options not available",
  [PMF_FEEDBACK_OPTION.SETTING_UNLIMITED_STOCK_IS_NOT_POSSIBLE]:
    "Setting unlimited stock is not possible",
  [PMF_FEEDBACK_OPTION.SUPPORTS_MULTIPLE_PHOTOS]: "Supports multiple photos",
  [PMF_FEEDBACK_OPTION.SUPPORTS_VIDEO]: "Supports video",
  [PMF_FEEDBACK_OPTION.TEMPLATE_IS_EASY_TO_USE]: "Template is easy to use",
  [PMF_FEEDBACK_OPTION.TOO_COMPLEX]: "Too complex",
  [PMF_FEEDBACK_OPTION.TOO_MANY_INPUTS_REQUIRED]: "Too many inputs required",
  [PMF_FEEDBACK_OPTION.TOO_MANY_STEPS]: "Too many steps",
  [PMF_FEEDBACK_OPTION.UPDATING_PRICE_AND_STOCK_IS_EASY]:
    "Updating price and stock is easy",
  [PMF_FEEDBACK_OPTION.UPDATING_STOCK_ACROSS_STORES_IS_STRAIGHTFORWARD]:
    "Updating stock across stores is straightforward",
  [PMF_FEEDBACK_OPTION.UPDATING_STOCK_FOR_ONE_ITEM_AT_A_TIME_IS_PAINFUL]:
    "Updating stock for one item at a time is painful",
}

/**
 * TEMPORARY (BP-63014) — English fallbacks for the GENERAL prompt's feature
 * radio labels. Same rationale as `PMF_FEEDBACK_SELECTION_FALLBACKS` above:
 * the PhraseApp keys are mapped but not yet published, so the labels would
 * render blank until `phrase pull` regenerates the locale files.
 *
 * TODO(BP-63014): delete this object and read `featureLabels` directly once the
 * copy is published and pulled. Confirm with
 * `yarn check-missing-locales:fresh --strict`.
 */
export const PMF_FEEDBACK_FEATURE_FALLBACKS: Record<
  PmfFeedbackFeatureChoice,
  string
> = {
  [PMF_FEEDBACK_EVENT.ADD_PRODUCT]: "Creating a product",
  [PMF_FEEDBACK_EVENT.UPDATE_PRODUCT]: "Editing a product",
  [PMF_FEEDBACK_EVENT.UPDATE_STOCK]: "Updating stocks",
  [PMF_FEEDBACK_EVENT.BULK_ADD_PRODUCT]: "Adding products in bulk",
  [PMF_FEEDBACK_EVENT.BULK_UPDATE_STOCK]: "Updating stocks in bulk",
  [PMF_FEEDBACK_FEATURE_OTHERS]: "Others",
}
